import React, { useState, useEffect } from 'react';
import { capitalize } from 'lodash';
import { gql, useMutation, useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const CREATE_DASHBOARD_TABLE = gql`
    mutation CreateDashboardTable($param: DashboardUpdateInput) {
        createDashboardTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const CREATE_STATISTICS_TABLE = gql`
    mutation CreateStatisticTable($param: StatisticUpdateInput) {
        createStatisticTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const CREATE_BOWTIE_DASHBOARD_TABLE = gql`
    mutation CreateBowtieDashboardTable($param: BowtieUpdateInput) {
        createBowtieDashboardTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const CREATE_BOWTIE_TABLE = gql`
    mutation CreateBowtieTable($param: BowtieUpdateInput) {
        createBowtieTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const UPDATE_ACTIVITY_TABLE = gql`
    mutation updateActivityTable($param: ActivityUpdateInput) {
        updateActivityTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const CREATE_SECE_STATUS_TABLE = gql`
    mutation CreateSeceStatusTable($param: SeceStatusUpdateInput) {
        createSeceStatusTable(param: $param) {
            success
            status_code
            message
            inner_error
        }
    }
`;

const GET_BATCH_OPERATION_STATUS = gql`
    query BatchOperationStatus($type: String!) {
        batchOperationStatus(type: $type) {
            type
            status
            message
            inner_error
            started_at
            completed_at
        }
    }
`;

const DASHBOARD = "dashboard";
const STATISTIC = "statistics";
const BOWTIE_DASHBOARD = "bowtie-dashboard";
const BOWTIE = "bowtie";
const ACTIVITY = "activity";
const SECE_STATUS = "sece-status";

function BatchOperations(props) {
    const [showHelp, setShowHelp] = useState(false);
    const [batchOperationName, setBatchOperationName] = useState("");
    const [batchOperationMode, setBatchOperationMode] = useState(false);
    const [statusType, setStatusType] = useState("");
    const [isPolling, setIsPolling] = useState(false);

    const [elapsedTime, setElapsedTime] = useState(0);
    const [isInProgress, setIsInProgress] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);
    const [innerErrorMessage, setInnerErrorMessage] = useState("");
    const [statusWarning, setStatusWarning] = useState("");

    const [createDashboardTable] = useMutation(CREATE_DASHBOARD_TABLE);
    const [createStatisticTable] = useMutation(CREATE_STATISTICS_TABLE);
    const [createBowtieDashboardTable] = useMutation(CREATE_BOWTIE_DASHBOARD_TABLE);
    const [createBowtieTable] = useMutation(CREATE_BOWTIE_TABLE);
    const [updateActivityTable] = useMutation(UPDATE_ACTIVITY_TABLE);
    const [createSeceStatusTable] = useMutation(CREATE_SECE_STATUS_TABLE);

    const { data: statusData, error: statusError } = useQuery(GET_BATCH_OPERATION_STATUS, {
        variables: { type: statusType },
        skip: !isPolling || !statusType,
        pollInterval: 5000,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (!isInProgress) {
            return undefined;
        }

        const startTime = Date.now();
        const timeIntervalId = setInterval(() => {
            setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);

        return () => clearInterval(timeIntervalId);
    }, [isInProgress, batchOperationName]);

    useEffect(() => {
        const status = statusData?.batchOperationStatus;

        if (!isPolling || !status) {
            return;
        }

        setStatusWarning("");

        if (status.status === 'succeeded') {
            setIsSuccess(true);
            setIsError(false);
            setInnerErrorMessage("");
            setIsInProgress(false);
            setIsPolling(false);
            return;
        }

        if (status.status === 'failed') {
            setIsSuccess(false);
            setIsError(true);
            setInnerErrorMessage(status.inner_error || status.message || 'The batch operation failed.');
            setIsInProgress(false);
            setIsPolling(false);
            return;
        }

        if (status.status === 'idle') {
            setIsSuccess(false);
            setIsError(true);
            setInnerErrorMessage('The API restarted before the batch status could be confirmed. Check the batch history before trying again.');
            setIsInProgress(false);
            setIsPolling(false);
        }
    }, [isPolling, statusData]);

    useEffect(() => {
        if (statusError && isPolling) {
            setStatusWarning('The status check was interrupted. Retrying automatically...');
        }
    }, [isPolling, statusError]);

    const operationConfig = {
        [DASHBOARD]: {
            apiType: 'Dashboard',
            run: createDashboardTable,
            resultKey: 'createDashboardTable'
        },
        [STATISTIC]: {
            apiType: 'Statistic',
            run: createStatisticTable,
            resultKey: 'createStatisticTable'
        },
        [BOWTIE_DASHBOARD]: {
            apiType: 'BowtieDashboard',
            run: createBowtieDashboardTable,
            resultKey: 'createBowtieDashboardTable'
        },
        [BOWTIE]: {
            apiType: 'Bowtie',
            run: createBowtieTable,
            resultKey: 'createBowtieTable'
        },
        [ACTIVITY]: {
            apiType: 'Activity',
            run: updateActivityTable,
            resultKey: 'updateActivityTable'
        },
        [SECE_STATUS]: {
            apiType: 'SeceStatus',
            run: createSeceStatusTable,
            resultKey: 'createSeceStatusTable'
        }
    };

    const failOperation = (message) => {
        setIsSuccess(false);
        setIsError(true);
        setInnerErrorMessage(message || 'The batch operation could not be started.');
        setIsInProgress(false);
        setIsPolling(false);
    };

    const batchOperationManager = async (operationType) => {
        const operation = operationConfig[operationType];

        if (!operation) {
            return;
        }

        setIsInProgress(true);
        setBatchOperationName(operationType);
        setStatusType("");
        setIsPolling(false);
        setIsSuccess(false);
        setIsError(false);
        setInnerErrorMessage("");
        setStatusWarning("");
        setElapsedTime(0);

        try {
            const response = await operation.run({ variables: { param: {} } });
            const result = response?.data?.[operation.resultKey];

            if (!result) {
                failOperation('The API returned no batch start result.');
                return;
            }

            if (result.status_code === 202 || result.status_code === 409) {
                setStatusType(operation.apiType);
                setIsPolling(true);

                if (result.status_code === 409) {
                    setStatusWarning('This batch was already running. Monitoring the existing run...');
                }
                return;
            }

            if (result.success === true) {
                setIsSuccess(true);
                setIsInProgress(false);
                return;
            }

            failOperation(result.inner_error || result.message);
        } catch (error) {
            failOperation(
                error?.message || 'The request to start the batch operation failed.'
            );
        }
    };

    return (
        <div className="w-full px-8 pb-8">
            <div className="pb-6 border-b-2 border-gray-200">
                <h1 className="text-blue-900 font-sans text-2xl font-bold uppercase">Batch Operations <FontAwesomeIcon
                    icon={['fas', 'info-circle']} className="ml-1 text-blue-900 cursor-pointer"
                    onClick={() => setShowHelp(!showHelp)} /></h1>

                {showHelp &&
                    <div>
                        <p className="mt-3">This page allows you to perform batch operations.</p>
                    </div>
                }
            </div>

            <h3 className="mt-4 text-blue-900 font-sans text-lg font-bold uppercase">Current View</h3>
            <h6 className="mb-6 font-bold text-sm text-red-900">Dashboard - Admin - Batch Operations</h6>


            <div className="mt-4 text-blue-900 font-sans text-sm font-bold">Batch Operation Mode</div>
            <div className={"relative block w-10 mr-2 align-left select-none transition duration-200 ease-in"}>

                <input type="checkbox"
                    name={"header_id"} id={"header_id"}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 border-red-300 appearance-none cursor-pointer"
                    checked={batchOperationMode}
                    onChange={(event) => { setBatchOperationMode(!batchOperationMode) }}
                />
                <label htmlFor={"header_id"} className="toggle-label block overflow-hidden h-6 rounded-full bg-red-300 cursor-pointer"></label>
            </div>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === DASHBOARD ? " underline" : "")}`}
                onClick={() => batchOperationManager(DASHBOARD)}
            >Create Dashboard Table
            </button>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === STATISTIC ? " underline" : "")}`}
                onClick={() => batchOperationManager(STATISTIC)}
            > Create Statistics Table
            </button>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === BOWTIE_DASHBOARD ? " underline" : "")}`}
                onClick={() => batchOperationManager(BOWTIE_DASHBOARD)}
            > Create Bowtie Dashboard Table
            </button>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === BOWTIE ? " underline" : "")}`}
                onClick={() => batchOperationManager(BOWTIE)}
            > Create Bowtie Table
            </button>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === ACTIVITY ? " underline" : "")}`}
                onClick={() => batchOperationManager(ACTIVITY)}
            > Create Activity Table
            </button>

            <button
                disabled={isInProgress || !batchOperationMode}
                type="button"
                className={`${"mt-3 button-red mr-2 mb-2 py-3 px-5" + (isInProgress || !batchOperationMode ? " cursor-not-allowed" : "") + (batchOperationName === SECE_STATUS ? " underline" : "")}`}
                onClick={() => batchOperationManager(SECE_STATUS)}
            > Create SECE Status Snapshot
            </button>

            <div className="min-h-screen bg-gray-10 flex flex-col justify-center items-center">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
                    <h1 className="text-center text-blue-900 text-2xl font-bold mb-4">{capitalize(batchOperationName)} Batch Operation Progress</h1>
                    {!isSuccess && !isError && isInProgress && (
                        <>
                            <div className="relative pt-1 mb-4">
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                                    <div className="w-full shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 animate-pulse" />
                                </div>
                            </div>
                            <p className="text-sm font-medium text-gray-600">
                                The batch is running safely in the background. This page checks its status automatically.
                                {elapsedTime > 0 && ` Elapsed time: ${elapsedTime}s`}
                            </p>
                            {statusWarning && (
                                <p className="mt-3 text-sm font-medium text-yellow-700">{statusWarning}</p>
                            )}
                        </>
                    )}
                    {isSuccess && (
                        <>
                            <p className="text-sm font-medium text-green-600 mb-4">Batch operation completed successfully!</p>
                            <button
                                className="button-red hover:bg-red-800 text-white font-semibold py-2 px-4"
                                onClick={() => { batchOperationManager(batchOperationName) }}
                            >
                                Start Over
                            </button>
                        </>
                    )}
                    {isError && (
                        <>
                            <p className="text-sm font-medium text-red-600 mb-4">There was an error during the batch operation.</p>
                            <button
                                className="button-red hover:bg-red-600 text-white font-semibold py-2 px-4 mr-6"
                                onClick={() => { batchOperationManager(batchOperationName) }}
                            >
                                Try Again
                            </button>
                            <button
                                className="button-red hover:bg-red-600 text-white font-semibold py-2 px-4"
                                onClick={() => { alert(innerErrorMessage) }}
                            >
                                View Error Message
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BatchOperations;